import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import App from './App';
import { ThemeProvider, getInitialTheme } from './contexts/ThemeContext';
import { msalConfig } from './config/authConfig';
import { featureFlags } from './config/featureFlags';
import '@celebration/react/index.css';
import '@celebration/assets/icons/celebration-fonts.css';
import './global.scss';

// Set theme before first paint to avoid flash
document.documentElement.setAttribute('data-theme', getInitialTheme());

// Global error handler reference (will be set by FeedbackProvider)
let globalErrorHandler: ((message: string) => void) | null = null;

export const setGlobalErrorHandler = (handler: (message: string) => void) => {
  globalErrorHandler = handler;
};

// Create QueryClient with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        // Extract error message
        let errorMessage = 'An unexpected error occurred';

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = (error as { message: string }).message;
        }

        // Show error via global handler if available
        if (globalErrorHandler) {
          globalErrorHandler(errorMessage);
        }
      },
    },
  },
});

/**
 * Render application with or without SSO based on feature flag
 */
const renderApp = () => {
  const root = ReactDOM.createRoot(document.getElementById('root')!);

  if (featureFlags.ssoEnabled) {
    // SSO Mode: Initialize MSAL and wrap app with MsalProvider
    console.log('🔐 SSO Mode: Azure AD authentication enabled');
    const msalInstance = new PublicClientApplication(msalConfig);

    msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise()
        .then((result) => {
          if (result) {
            // After a successful OAuth redirect, restore the original deep-link.
            // replaceState must run before root.render so BrowserRouter reads
            // the correct pathname on first mount — MSAL always redirects back
            // to the root redirectUri, so we manually restore the original path here.
            const redirectPage = sessionStorage.getItem('alloc.redirectStartPage');
            if (redirectPage) {
              sessionStorage.removeItem('alloc.redirectStartPage');
              const target = new URL(redirectPage);
              window.history.replaceState(null, '', target.pathname + target.search + target.hash);
            }
          }
        })
        .catch((error) => {
          console.error('Redirect error:', error);
        })
        .finally(() => {
          root.render(
            <React.StrictMode>
              <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                  <MsalProvider instance={msalInstance}>
                    <ThemeProvider>
                      <App />
                    </ThemeProvider>
                  </MsalProvider>
                </BrowserRouter>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </React.StrictMode>,
          );
        });
    }).catch((error) => {
      console.error('MSAL initialization failed:', error);
    });
  } else {
    // Dev Mode: No SSO, render app directly without MsalProvider
    console.log('🔓 Dev Mode: SSO authentication disabled');
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
};

// Start the application (only in browser, not in test environment)
if (import.meta.env.MODE !== 'test') {
  renderApp();
}
